import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';

@Component({
  selector: 'fl-ai-modal',
  template: `
    <div class="Container">
      <div
        class="Header"
        [flMarginBottom]="'mid'"
      >
        <div class="Header-subcontainer">
          <fl-icon
            [clickable]="true"
            [name]="'ui-chevron-left'"
            [useIconFont]="true"
            [fillIconFont]="true"
            [size]="IconSize.SMALL"
            [flMarginRight]="'xxsmall'"
            (click)="triggerGoBack()"
          ></fl-icon>
          <p class="font-medium">
            <ng-container *ngIf="!subFunctionality">{{
              functionality
            }}</ng-container>
            <ng-container *ngIf="subFunctionality">
              {{ functionality }}: {{ subFunctionality }}
            </ng-container>
          </p>
        </div>
        <fl-icon
          [name]="'ui-close'"
          [clickable]="true"
          [clickAnimation]="'none'"
          [hoverAnimation]="'spin'"
          [size]="IconSize.SMALL"
          (click)="triggerCancel()"
        ></fl-icon>
      </div>
      <div
        class="TextareaContainer"
        [flMarginBottom]="'mid'"
      >
        <fl-textarea
          class="Textarea"
          [disabled]="isGenerating"
          [control]="control"
          [autoGrow]="true"
          [autoGrowMaxHeight]="152"
          [rows]="1"
        >
        </fl-textarea>
        <fl-icon
          *ngIf="isRecording"
          class="Spinner"
          [flMarginRight]="'xxsmall'"
          [name]="'ui-stop-circle'"
          [color]="IconColor.ERROR"
          [clickable]="true"
          [useIconFont]="true"
          [size]="IconSize.SMALL"
          (click)="triggerStopRecording()"
        ></fl-icon>
        <fl-spinner
          *ngIf="isGenerating && !isRecording"
          class="Spinner"
          [size]="SpinnerSize.SMALL"
          [color]="SpinnerColor.PINK"
        >
        </fl-spinner>
      </div>
      <ng-container *ngIf="errorState; else default">
        <div
          class="ErrorContainer"
          [flMarginBottom]="'small'"
        >
          <fl-icon
            class="ButtonIcon"
            [flMarginRight]="'xxsmall'"
            [name]="'ui-error'"
            [color]="IconColor.ERROR"
            [useIconFont]="true"
            [size]="IconSize.SMALL"
          ></fl-icon>
          <p
            class="text-error"
            i18n="Error state"
          >
            Unable to complete the generation, please try again later.
          </p>
        </div>
        <fl-button
          i18n="Regenerate button"
          [color]="ButtonColor.DEFAULT"
          [size]="ButtonSize.SMALL"
          (click)="triggerRegenerateAction()"
        >
          <fl-icon
            class="ButtonIcon"
            [flMarginRight]="'xxxsmall'"
            [name]="'ui-refresh'"
            [color]="IconColor.INHERIT"
            [size]="IconSize.SMALL"
          ></fl-icon>
          Regenerate
        </fl-button>
      </ng-container>
      <ng-template #default>
        <div
          *ngIf="showCTAOptions"
          class="ButtonsContainer"
        >
          <fl-button
            [flMarginRight]="'xxsmall'"
            [color]="ButtonColor.DEFAULT"
            [size]="ButtonSize.SMALL"
            (click)="triggerLeftButton()"
          >
            <fl-icon
              class="ButtonIcon"
              [flMarginRight]="'xxxsmall'"
              [name]="leftButtonIcon"
              [color]="IconColor.INHERIT"
              [size]="IconSize.SMALL"
            ></fl-icon>
            {{ leftButtonCopy }}
          </fl-button>

          <fl-button
            [color]="ButtonColor.DEFAULT"
            [size]="ButtonSize.SMALL"
            (click)="triggerRightButton()"
          >
            <fl-spinner
              *ngIf="speechToTextGenerating"
              [size]="SpinnerSize.SMALL"
              [color]="SpinnerColor.PINK"
            >
            </fl-spinner>
            <fl-icon
              *ngIf="!speechToTextGenerating"
              class="ButtonIcon"
              [flMarginRight]="'xxxsmall'"
              [name]="rightButtonIcon"
              [color]="IconColor.INHERIT"
              [useIconFont]="true"
              [size]="IconSize.XSMALL"
            ></fl-icon>

            {{ rightButtonCopy }}
          </fl-button>
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: ['./ai-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiModalComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  IconSize = IconSize;
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;

  @Input() functionality?: string;
  @Input() subFunctionality?: string;

  @Input() alternativeAction = true;
  @Input() primaryIcon: string;
  @Input() secondaryIcon: string;
  @Input() isRecording = false;
  @Input() showCTAOptions = false;

  // By default we will use this copy and icons since this would be the most common pattern
  @Input() leftButtonCopy = $localize`Insert after`;
  @Input() leftButtonIcon = 'ui-insert';
  @Input() rightButtonCopy = $localize`Replace selection`;
  @Input() rightButtonIcon = 'ui-tick';
  @Input() control: FormControl<string>;
  @Input() isGenerating = false;
  @Input() errorState = false;
  @Input() speechToTextGenerating = false;

  @Output() clickRegenerate = new EventEmitter<void>();
  @Output() clickGoBack = new EventEmitter<void>();
  @Output() clickLeftButton = new EventEmitter<void>();
  @Output() clickRightButton = new EventEmitter<void>();
  @Output() clickDiscard = new EventEmitter<void>();
  @Output() clickCancel = new EventEmitter<void>();
  @Output() clickTriggerModal = new EventEmitter<void>();
  @Output() clickIcon = new EventEmitter<void>();
  @Output() clickStopRecording = new EventEmitter<void>();

  showPrimaryIcon = true;

  triggerGoBack(): void {
    this.clickGoBack.emit();
  }

  triggerLeftButton(): void {
    this.clickLeftButton.emit();
  }

  triggerRightButton(): void {
    this.clickRightButton.emit();
  }

  triggerRegenerateAction(): void {
    this.clickRegenerate.emit();
  }

  triggerCancel(): void {
    this.clickCancel.emit();
  }

  triggerDiscard(): void {
    this.clickDiscard.emit();
  }

  toggleAlternativeActionIcon(): void {
    this.showPrimaryIcon = !this.showPrimaryIcon;
    this.clickIcon.emit();
  }

  triggerStopRecording(): void {
    this.clickStopRecording.emit();
  }
}
